import {GA4EventName, GA4EventParamName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import type {GA4EventSources} from '@Libraries/ga-events';
import type {CutoutRefineEntryPoint, CutoutRefineExit} from '@PosterWhiteboard/poster/poster-cutout-refine/poster-cutout-refine.types';
import type {CutoutRefineTool} from '@PosterWhiteboard/classes/item-refinement.types';
import {CUTOUT_REFINE_GA_TOOLS_BOTH, CUTOUT_REFINE_GA_TOOLS_NONE, CUTOUT_REFINE_GA_TYPE_BY_ENTRY_POINT} from '@PosterWhiteboard/poster/poster-cutout-refine/poster-cutout-refine.types';

/**
 * GA4 for one Eraser session: what opened it, what the user did while it was open, and how it ended.
 * Single strokes and toggles are folded into the closing event rather than sent one by one.
 */
export class PosterCutoutRefineAnalytics {
  private gaType = '';
  private toolsUsed = new Set<CutoutRefineTool>();
  private hasReset = false;
  private hasShownOriginal = false;

  public trackOpened(entryPoint: CutoutRefineEntryPoint, source: GA4EventSources): void {
    this.gaType = CUTOUT_REFINE_GA_TYPE_BY_ENTRY_POINT[entryPoint];
    this.toolsUsed = new Set();
    this.hasReset = false;
    this.hasShownOriginal = false;
    trackPosterBuilderGA4Events(GA4EventName.ERASER_OPENED, {[GA4EventParamName.SOURCE]: source, [GA4EventParamName.TYPE]: this.gaType});
  }

  public recordPaint(tool: CutoutRefineTool): void {
    this.toolsUsed.add(tool);
  }

  public recordReset(): void {
    this.hasReset = true;
  }

  public recordShowOriginal(): void {
    this.hasShownOriginal = true;
  }

  public trackApplied(exit: CutoutRefineExit): void {
    trackPosterBuilderGA4Events(GA4EventName.ERASER_APPLIED, {
      [GA4EventParamName.TYPE]: this.gaType,
      [GA4EventParamName.TOOLS]: this.#getToolsValue(),
      [GA4EventParamName.EXIT]: exit,
      [GA4EventParamName.RESET]: `${this.hasReset}`,
      [GA4EventParamName.ORIGINAL]: `${this.hasShownOriginal}`,
    });
  }

  public trackCancelled(hadEdits: boolean): void {
    trackPosterBuilderGA4Events(GA4EventName.ERASER_CANCELLED, {[GA4EventParamName.TYPE]: this.gaType, [GA4EventParamName.HAD_EDITS]: `${hadEdits}`});
  }

  #getToolsValue(): string {
    if (this.toolsUsed.size > 1) {
      return CUTOUT_REFINE_GA_TOOLS_BOTH;
    }
    const [onlyTool] = this.toolsUsed;
    return onlyTool ?? CUTOUT_REFINE_GA_TOOLS_NONE;
  }
}
